import { ActivatedRoute, Router } from '@angular/router';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { SendInBlueService } from '../../services/send-in-blue.service';
import { emailRule } from '@up/shared/helpers';
import html2pdf from 'html2pdf.js';
import {
    faFacebookF,
    faInstagram,
    faLinkedinIn,
    faYoutube,
    faTiktok,
    IconDefinition
} from '@fortawesome/free-brands-svg-icons';
import { faLink } from '@fortawesome/free-solid-svg-icons';
import { GiftCardTopUpService } from "../../services/gift-card-top-up.service";
import { animate, state, style, transition, trigger } from "@angular/animations";

const HTML2PDF_OPTIONS = {
  html2canvas: {
    allowTaint: true,
    scale: 2,
    useCORS: true
  },
  margin: 1,
  filename: `Receipt.pdf`
};

const faXTwitter: IconDefinition = {
  prefix: 'fab',
  iconName: 'twitter',
  icon: [24, 24, [], '', 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z']
};

@Component({
  selector: 'up-gift-card-receipt',
  templateUrl: './gift-card-receipt.component.html',
  styleUrls: ['./gift-card-receipt.component.scss'],
  animations: [
    trigger('cardAnimation', [
      state('visible', style({
        opacity: 1,
        transform: 'scale(1)',
        height: '*'
      })),
      state('hidden', style({
        opacity: 0,
        transform: 'scale(0.95)',
        height: '0px',
        overflow: 'hidden'
      })),
      transition('visible => hidden', [
        animate('0.5s cubic-bezier(0.35, 0, 0.25, 1)')
      ]),
      transition('hidden => visible', [
        animate('0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)')
      ])
    ]),
    trigger('slideDown', [
      transition(':enter', [
        style({
          height: '0px',
          opacity: 0,
          overflow: 'hidden'
        }),
        animate('0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)', style({
          height: '*',
          opacity: 1
        }))
      ])
    ])
  ]
})
export class GiftCardReceiptComponent implements OnInit {
  constructor(
    private route: ActivatedRoute,
    private ref: ChangeDetectorRef,
    private router: Router,
    private giftCardTupUpService: GiftCardTopUpService,
    private sendInBlueService: SendInBlueService
  ) {
    const params = this.route.snapshot.queryParams;
    this.refId = params.refId;
  }

  currentDate = new Date();
  public refId: string;
  public topup: any;
  public loading = true;
  public sendReceipt = false;
  public emailAddress = '';
  public receiptSent = false;
  public isSendingEmail = false;
  public isSavingPDF = false;
  public shouldSaveReceipt = false;
  public emailRegex = emailRule;
  public notificationAnimationState = 'hidden';
  googleWalletLink = 'javascript:void(0)';
  appleWalletLink = 'javascript:void(0)';

  async ngOnInit() {
    await this.initialize();
  }

  async initialize() {
      this.giftCardTupUpService
        .getTopupById(this.refId)
        .subscribe(
          async (t) => {
            this.topup = t;
            this.appleWalletLink = t.giftCard.appleWalletS3Link;
            this.googleWalletLink = t.giftCard.googleWalletJwtLink;
            this.loading = false;
          },
          (error) => {
            this.loading = false;
            console.log(error);
            this.router.navigate(['/error']);
            return;
          }
        );
  }

  saveAsPDF() {
    this.isSavingPDF = true;

    const element = document.getElementById('pdf-content');
    html2pdf()
      .from(element)
      .set(HTML2PDF_OPTIONS)
      .save()
      .then(() => {
        this.isSavingPDF = false;
        this.ref.detectChanges();
      });
  }

  toggleDownloadSection(): void {
    this.shouldSaveReceipt = !this.shouldSaveReceipt;
  }

  toggleReceiptSentNotification() {
    if (this.notificationAnimationState === 'visible') {
      this.notificationAnimationState = 'hidden';
      this.receiptSent = false;
    } else {
      this.notificationAnimationState = 'visible';
      this.receiptSent = true;
    }
  }

  async sendEmail() {
    this.isSendingEmail = true;

    const element = document.getElementById('pdf-content');
    html2pdf()
      .from(element)
      .set(HTML2PDF_OPTIONS)
      .toPdf()
      .output('datauristring')
      .then(async (pdfAsString: string) => {
        this.sendInBlueService
          .sendEmail(pdfAsString.split(',')[1], this.emailAddress)
          .subscribe((_) => {
            this.toggleReceiptSentNotification();
            this.toggleDownloadSection();
            this.isSendingEmail = false;
            this.sendReceipt = false;
            this.ref.detectChanges();
            window.scrollTo(0, 0);
          });
      });
  }

  getDeviceType(): 'android' | 'ios' | 'other' {
    const ua = navigator.userAgent.toLowerCase();
    if (/android/.test(ua)) return 'android';
    if (/iphone|ipad|ipod/.test(ua)) return 'ios';
    return 'other';
  }

  getSocialIcon(socialName: string) {
    const iconMap: { [key: string]: IconDefinition } = {
      'Facebook': faFacebookF,
      'Instagram': faInstagram,
      'Twitter': faXTwitter,
      'X': faXTwitter,
      'LinkedIn': faLinkedinIn,
      'YouTube': faYoutube,
      'TikTok': faTiktok
    };

    return iconMap[socialName] || faLink;
  }

  downloadApp() {
    window.open('https://apps.apple.com/us/app/crave-cards/id1576343606', '_blank');
  }

  getCurrencySymbol(currencyCode: string): string {
    const currencies = {
      '036': 'AUD', '124': 'CAD', '156': 'CNY', '203': 'CZK',
      '208': 'DKK', '344': 'HKD', '348': 'HUF', '356': 'INR',
      '392': 'JPY', '410': 'KRW', '484': 'MXN', '554': 'NZD',
      '578': 'NOK', '702': 'SGD', '710': 'ZAR', '752': 'SEK',
      '756': 'CHF', '826': 'GBP', '840': 'USD', '978': 'EUR',
      '986': 'BRL', '985': 'PLN', '949': 'TRY', '682': 'SAR',
      '784': 'AED', '196': 'CYP', '191': 'HRK', '643': 'RUB'
    };
    return currencies[currencyCode] || currencyCode;
  }
}
